import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import { CREDIT_GRANT_DATA, type CreditGrantData } from '@shared/data';

interface CreditGrantsSectionProps {
  isDetailView?: boolean;
  className?: string;
}

const renderAvailable = (value: unknown) => {
  const amount = typeof value === 'string' ? value : '$0';
  return (
    <span className="font-medium">
      {amount}
    </span>
  );
};

const renderStatus = (_: unknown, item: CreditGrantData) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge
      entityType="credit-grant"
      status={item.status}
      size="sm"
    />
  </div>
);

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'success'> = {
  high: 'error',
  medium: 'warning',
  low: 'success',
};

const renderPriority = (_: unknown, item: CreditGrantData) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge
      variant="generic"
      text={item.priority}
      color={PRIORITY_COLORS[item.priority.toLowerCase()] ?? 'neutral'}
      size="sm"
    />
  </div>
);

export const CreditGrantsSection = React.memo<CreditGrantsSectionProps>(({ isDetailView = false, className }) => {
  const columns: PanelTableColumn<CreditGrantData>[] = [
    {
      key: 'name',
      label: 'NAME',
      headerClassName: 'w-[130px]',
      cellClassName: 'font-medium'
    },
    {
      key: 'description',
      label: 'DESCRIPTION',
      headerClassName: 'w-[200px]',
      cellClassName: 'text-muted-foreground truncate max-w-[200px]'
    },
    {
      key: 'available',
      label: 'AVAILABLE',
      headerClassName: 'w-[90px]',
      render: renderAvailable
    },
    {
      key: 'status',
      label: 'STATUS',
      headerClassName: 'w-[80px]',
      render: renderStatus
    },
    {
      key: 'priority',
      label: 'PRIORITY',
      headerClassName: 'w-[90px]',
      render: renderPriority
    },
    {
      key: 'effectiveDate',
      label: 'EFFECTIVE',
      headerClassName: 'w-[110px]',
      cellClassName: 'whitespace-nowrap text-muted-foreground'
    },
    {
      key: 'expiryDate',
      label: 'EXPIRY',
      headerClassName: 'w-[110px]',
      cellClassName: 'whitespace-nowrap text-muted-foreground'
    }
  ];

  const rowActions = (item: CreditGrantData) => [
    {
      key: 'update',
      label: 'Update',
      onClick: () => console.log('Update credit grant', item)
    },
    {
      key: 'void',
      label: 'Void',
      onClick: () => console.log('Void credit grant', item)
    }
  ];

  return (
    <SectionCard title="Credit Grants" layout="down" className={className}>
      <PanelTable
        data={CREDIT_GRANT_DATA}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No credit grants found"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

CreditGrantsSection.displayName = 'CreditGrantsSection';